// ============================================================
//  INSURANCE DEFAULTS PANEL — admin dashboard
//  Path: src/pages/InsuranceDefaultsPanel.tsx  (next to CategoriesPage.tsx)
//
//  One default fee per insurance type (Liability, Cargo, Commercial Auto,
//  Professional Indemnity). Saving it copies the value into every category
//  that has not been given its own value. A category edited by hand keeps
//  its value and is listed under its type with a "Reset to default" button.
//
//  Use in CategoriesPage.tsx, right under the "Platform Defaults" block:
//    import InsuranceDefaultsPanel from './InsuranceDefaultsPanel'
//    {activeTab === 'basics' && <InsuranceDefaultsPanel canModify={!!canModify} onChanged={fetchCategories} />}
//  (use the name of the function that reloads the category list)
// ============================================================
import { useEffect, useState } from 'react'
import { listInsuranceDefaults, setInsuranceDefault, resetCategoryInsuranceFeeToDefault } from '../services/admin.service'
import type { InsuranceDefaultInfo } from '../types'

type InsType = InsuranceDefaultInfo['insuranceType']
type TypeInfo = InsuranceDefaultInfo

const LABELS: Record<InsType, string> = {
  liability: 'Liability',
  cargo: 'Cargo',
  commercial_auto: 'Commercial Auto',
  professional_indemnity: 'Professional Indemnity',
}

interface Draft { feeType: 'percent' | 'flat'; feeValue: string; customerPct: string; taskerPct: string; waivedIfVerified: boolean }

const toDraft = (d: InsuranceDefaultInfo['default']): Draft => ({
  feeType: d?.feeType ?? 'percent',
  feeValue: String(d?.feeValue ?? 0),
  customerPct: String(d?.customerPct ?? 0),
  taskerPct: String(d?.taskerPct ?? 100),
  waivedIfVerified: d?.waivedIfVerified ?? true,
})

export default function InsuranceDefaultsPanel({ canModify, onChanged }: { canModify: boolean; onChanged?: () => void }) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<TypeInfo[] | null>(null)
  const [drafts, setDrafts] = useState<Partial<Record<InsType, Draft>>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = async () => {
    try {
      const res = await listInsuranceDefaults()
      const list: InsuranceDefaultInfo[] = res.data.data
      setItems(list)
      const next: Partial<Record<InsType, Draft>> = {}
      list.forEach((i) => { next[i.insuranceType] = toDraft(i.default) })
      setDrafts(next)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load insurance defaults')
    }
  }

  useEffect(() => { if (open && !items) load() }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (info: TypeInfo) => {
    const d = drafts[info.insuranceType]
    if (!d || !canModify) return
    const feeValue = Number(d.feeValue)
    const customerPct = Number(d.customerPct)
    const taskerPct = Number(d.taskerPct)
    if (!Number.isFinite(feeValue) || feeValue < 0) { setError('The fee must be a number, 0 or more'); return }
    if (!Number.isInteger(customerPct) || !Number.isInteger(taskerPct) || customerPct + taskerPct !== 100) {
      setError('Customer % and Tasker % must be whole numbers that add up to 100'); return
    }
    const msg = `Set the ${LABELS[info.insuranceType]} default to ${feeValue}${d.feeType === 'percent' ? '%' : ' (flat)'}?\n\n` +
      `This changes ${info.followingDefault} categor${info.followingDefault === 1 ? 'y' : 'ies'} that follow the default. ` +
      `${info.withOwnValue} with their own value will not change.`
    if (!window.confirm(msg)) return
    setBusy(info.insuranceType); setError(null); setNotice(null)
    try {
      await setInsuranceDefault(info.insuranceType, {
        feeType: d.feeType, feeValue, customerPct, taskerPct, waivedIfVerified: d.waivedIfVerified,
      })
      setNotice(`${LABELS[info.insuranceType]} default saved and applied.`)
      await load(); onChanged?.()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save the default')
    } finally { setBusy(null) }
  }

  const reset = async (info: TypeInfo, categoryId: string, name: string) => {
    if (!canModify) return
    if (!window.confirm(`Put "${name}" back on the ${LABELS[info.insuranceType]} default?`)) return
    setBusy(categoryId); setError(null); setNotice(null)
    try {
      await resetCategoryInsuranceFeeToDefault(categoryId, info.insuranceType)
      setNotice(`${name} now follows the default.`)
      await load(); onChanged?.()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset')
    } finally { setBusy(null) }
  }

  const set = (t: InsType, patch: Partial<Draft>) => setDrafts((p) => ({ ...p, [t]: { ...(p[t] as Draft), ...patch } }))
  const inputCls = 'w-full bg-surface-tertiary border border-border-light rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50'

  return (
    <div className="bg-surface-card border border-border-light rounded-2xl mb-6 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div>
          <h2 className="text-sm font-semibold text-white">Insurance Defaults</h2>
          <p className="text-xs text-gray-500 mt-0.5">One default per insurance type. Every category follows it until you give that category its own value.</p>
        </div>
        <span className="text-gray-500 text-sm">{open ? '▲ Hide' : '▼ Edit'}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-border-light pt-4 space-y-5">
          {error && <p className="text-sm text-red-400">{error}</p>}
          {notice && <p className="text-sm text-green-400">{notice}</p>}
          {!items ? <p className="text-sm text-gray-400">Loading...</p> : items.map((info) => {
            const d = drafts[info.insuranceType] || toDraft(info.default)
            return (
              <div key={info.insuranceType} className="border border-border-light rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-white">{LABELS[info.insuranceType]}</h3>
                  <span className="text-xs text-gray-500">
                    {info.default ? `${info.followingDefault} follow the default · ${info.withOwnValue} own value` : 'No default set yet'}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Type</label>
                    <select disabled={!canModify} value={d.feeType} onChange={(e) => set(info.insuranceType, { feeType: e.target.value as 'percent' | 'flat' })} className={inputCls}>
                      <option value="percent">Percent of job</option>
                      <option value="flat">Flat amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Fee ({d.feeType === 'percent' ? '%' : '$'})</label>
                    <input type="number" step="0.01" min="0" disabled={!canModify} value={d.feeValue} onChange={(e) => set(info.insuranceType, { feeValue: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Customer pays %</label>
                    <input type="number" min="0" max="100" disabled={!canModify} value={d.customerPct}
                      onChange={(e) => { const v = e.target.value; set(info.insuranceType, { customerPct: v, taskerPct: String(100 - (Number(v) || 0)) }) }} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Tasker pays %</label>
                    <input type="number" min="0" max="100" disabled={!canModify} value={d.taskerPct}
                      onChange={(e) => { const v = e.target.value; set(info.insuranceType, { taskerPct: v, customerPct: String(100 - (Number(v) || 0)) }) }} className={inputCls} />
                  </div>
                  <button disabled={!canModify || busy === info.insuranceType} onClick={() => save(info)}
                    className="px-4 py-2 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-40">
                    {busy === info.insuranceType ? 'Saving…' : 'Save default'}
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-400 mt-3 cursor-pointer">
                  <input type="checkbox" disabled={!canModify} checked={d.waivedIfVerified} onChange={(e) => set(info.insuranceType, { waivedIfVerified: e.target.checked })} />
                  Waive the fee when the tasker has a verified policy
                </label>

                {info.ownValueCategories.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border-light">
                    <p className="text-xs text-gray-500 mb-1">Categories with their own value (not changed by the default):</p>
                    <div className="space-y-1">
                      {info.ownValueCategories.map((c) => (
                        <div key={c.id} className="flex items-center justify-between text-xs">
                          <span className="text-gray-300">{c.name} · {c.feeType === 'flat' ? `$${c.feeValue}` : `${c.feeValue}%`}</span>
                          {info.default && canModify && (
                            <button disabled={busy === c.id} onClick={() => reset(info, c.id, c.name)} className="text-brand-400 hover:text-brand-300 disabled:opacity-40">
                              Reset to default
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          <p className="text-xs text-gray-500">
            A default only changes categories that already have a row of that type. To add or remove a type for one category, use its own Insurance fees panel.
          </p>
        </div>
      )}
    </div>
  )
}